import { useMemo } from "react";

// `import type` only — see featureCatalog.ts's analogous note: a runtime
// import of @astrana/ui/modules (as MemberProfilePage.tsx has, for
// ModuleType/CreateClaim/etc.) does not resolve cleanly under Vitest's ESM
// resolver in every context. Extracting this hook to its own module (rather
// than leaving it inline in MemberProfilePage.tsx) keeps it unit-testable
// without pulling that runtime import in.
// eslint-disable-next-line import/no-unresolved
import type { ModuleGroup } from "@astrana/ui/modules";

/**
 * Returns the tab to open first, in priority order:
 *   1. the admin-configured landing tab (the "defaultTab" actions sentinel —
 *      see useConfiguredDefaultTab in access/useFeatureAccess.ts), if it names
 *      a tab that is actually visible;
 *   2. else "Home", if visible;
 *   3. else the first visible tab (callers should pass the FINAL sorted/
 *      visible tab list — see useTabOrder — so this follows the configured
 *      tab order too);
 *   4. else "Home" (unchanged final fallback). Prevents ADMIN persona (empty
 *      PERSONA_MODULES → visible = [Activity, Diagnoses, Labs]) from landing
 *      on "Home", which is not in its visible list.
 */
export function useInitialTab(
  visibleModules: ModuleGroup[],
  configuredDefaultTab: string | undefined,
): string {
  return useMemo(() => {
    const tabNames = visibleModules.map((g) => g.tabName);
    if (configuredDefaultTab && tabNames.includes(configuredDefaultTab)) {
      return configuredDefaultTab;
    }
    return tabNames.includes("Home") ? "Home" : (tabNames[0] ?? "Home");
  }, [visibleModules, configuredDefaultTab]);
}
